import React, { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { OverlayShell } from 'components/OverlayShell'
import { useOverlaySocketCredential } from 'hooks/useOverlaySocketCredential'
import {
  CameraCommandEnvelope,
  useCameraCommandEvents
} from 'hooks/useOverlaySubscription'
import { graphqlRequest } from 'lib/helpers'
import getCameraControlForRenderQuery from 'graphql/getCameraControlForRender.graphql'
import logger from 'logger'

const log = logger.extend('camctl-overlay')

const BOOTSTRAP_RETRY_MS = 15000

const TICK_MS = 200

const CAM_OP_BACKGROUND = '#0b0b0f'

interface CamControlRenderState {
  current: CameraCommandEnvelope | null
  queue: CameraCommandEnvelope[]
  status: string
  version: number
}

const pruneWins = (wins: CameraCommandEnvelope[], now: number): CameraCommandEnvelope[] => {
  const past = wins.filter((w) => w.showAt <= now)
  const future = wins.filter((w) => w.showAt > now)
  return past.length > 1 ? [...past.slice(-1), ...future] : wins
}

const commandFontSize = (command: string): string => {
  const perChar = 150 / Math.max(3, command.length)
  return `min(38vh, ${Math.min(34, perChar).toFixed(1)}vw)`
}

const CAM_PULSE_KEYFRAMES = `
@keyframes spCamPulse {
  0% { transform: scale(1.06); filter: brightness(1.9); }
  100% { transform: scale(1); filter: brightness(1); }
}
`

export const CamControlOverlayPage = (): JSX.Element => {
  const { slug, camControlId } = useParams<{ slug: string, camControlId: string }>()
  const token = useOverlaySocketCredential()

  const versionRef = useRef(-1)
  const [status, setStatus] = useState<string | null>(null)
  const [wins, setWins] = useState<CameraCommandEnvelope[]>([])
  const [now, setNow] = useState(() => Date.now())

  const applyRenderState = useCallback((state: CamControlRenderState): void => {
    if (typeof state?.version !== 'number') {
      log({ stage: 'bootstrap-shape', detail: 'renderState missing numeric version dropped' })
      return
    }
    if (state.version <= versionRef.current) {
      log({ stage: 'stale-drop', source: 'bootstrap', version: state.version, lastSeen: versionRef.current })
      return
    }
    versionRef.current = state.version
    setStatus(typeof state.status === 'string' ? state.status : 'INACTIVE')
    const list = [
      ...(state.current != null ? [state.current] : []),
      ...(Array.isArray(state.queue) ? state.queue : [])
    ]
    setWins(list.sort((a, b) => a.showAt - b.showAt))
  }, [])

  const refetchBootstrap = useCallback(async (): Promise<void> => {
    if (slug == null || slug === '' || camControlId == null || camControlId === '' || token == null || token === '') return
    const res = await graphqlRequest<{ renderState: unknown }>(
      getCameraControlForRenderQuery, { slug, camControlId }, token
    )
    if (res.renderState == null) {
      log({ stage: 'renderState-null', detail: 'unknown slug/camControlId, or a backend predating camera controls' })
      return
    }
    let state: CamControlRenderState
    try {
      state = typeof res.renderState === 'string'
        ? JSON.parse(res.renderState)
        : res.renderState as CamControlRenderState
    } catch (err) {
      log({ stage: 'renderState-parse', err: err instanceof Error ? err.name : 'unknown' })
      return
    }
    applyRenderState(state)
  }, [slug, camControlId, token, applyRenderState])

  useEffect(() => {
    if (slug == null || slug === '' || camControlId == null || camControlId === '' || token == null || token === '') {
      log({
        stage: 'token-missing',
        detail: 'slug / camControlId / ?token= missing page stays on the connecting state forever',
        hasSlug: slug != null && slug !== '',
        hasCamControlId: camControlId != null && camControlId !== '',
        hasToken: token != null && token !== ''
      })
    }
  }, [slug, camControlId, token])

  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof globalThis.setTimeout> | undefined
    const load = (): void => {
      refetchBootstrap()
        .catch((err: unknown) => {
          log({ stage: 'bootstrap-error', err: err instanceof Error ? err.name : 'unknown', retryInMs: BOOTSTRAP_RETRY_MS })
          if (!cancelled) retryTimer = globalThis.setTimeout(load, BOOTSTRAP_RETRY_MS)
        })
    }
    load()
    return () => {
      cancelled = true
      globalThis.clearTimeout(retryTimer)
    }
  }, [refetchBootstrap])

  const handleConnected = useCallback((): void => {
    void refetchBootstrap()
      .catch(() => { })
  }, [refetchBootstrap])

  const onCommand = useCallback((env: CameraCommandEnvelope): void => {
    if (env.version <= versionRef.current) {
      log({ stage: 'stale-drop', source: 'envelope', version: env.version, lastSeen: versionRef.current, seq: env.seq })
      return
    }
    versionRef.current = env.version
    if (env.origin === 'control') {
      log({ stage: 'control-envelope', version: env.version, seq: env.seq })
      if (env.status === 'ACTIVE' || env.status === 'INACTIVE') setStatus(env.status)
      void refetchBootstrap()
        .catch(() => { })
      return
    }
    setWins((prev) => pruneWins(
      [...prev.filter((w) => w.seq !== env.seq), env].sort((a, b) => a.showAt - b.showAt),
      Date.now()
    ))
  }, [refetchBootstrap])

  const channel = slug != null && slug !== '' && token != null && token !== '' ? `${slug}:camctl:overlay` : null
  const { connected } = useCameraCommandEvents(channel, token, camControlId ?? '', {
    onCommand,
    onConnected: handleConnected
  })

  useEffect(() => {
    const id = globalThis.setInterval(() => {
      const t = Date.now()
      setNow(t)
      setWins((prev) => {
        const pruned = pruneWins(prev, t)
        return pruned.length === prev.length ? prev : pruned
      })
    }, TICK_MS)
    return () => globalThis.clearInterval(id)
  }, [])

  useEffect(() => {
    document.body.style.background = CAM_OP_BACKGROUND
    return () => {
      document.body.style.background = 'transparent'
    }
  }, [])

  const pastWins = wins.filter((w) => w.showAt <= now)
  const current = pastWins.length > 0 ? pastWins[pastWins.length - 1] : null
  const upcoming = wins.find((w) => w.showAt > now) ?? null
  const stale = current !== null && now > current.showAt + current.ttlMs
  const stopped = status !== null && status !== 'ACTIVE'
  const dimmed = stopped || stale

  if (slug == null || slug === '' || camControlId == null || camControlId === '' || token == null || token === '') {
    return <OverlayShell>{null}</OverlayShell>
  }

  return (
    <OverlayShell>
      <style>{CAM_PULSE_KEYFRAMES}</style>
      <div
        data-testid='camctl-page'
        style={{
          position: 'fixed',
          inset: 0,
          background: CAM_OP_BACKGROUND,
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
        }}
      >
        <div
          data-testid='camctl-connection'
          style={{
            position: 'absolute',
            top: 16,
            right: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.55)'
          }}
        >
          {stopped && <span data-testid='camctl-stopped-label'>stopped</span>}
          <span>{connected ? 'live' : 're-syncing'}</span>
          <span
            data-testid='camctl-dot'
            style={{
              width: 10,
              height: 10,
              borderRadius: 9999,
              background: connected ? '#34d399' : '#fbbf24',
              boxShadow: connected ? '0 0 8px #34d39988' : '0 0 8px #fbbf2488',
              animation: connected ? undefined : 'spCamPulse 1.2s ease-in-out infinite'
            }}
          />
        </div>

        <div
          data-testid='camctl-readout'
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2vh',
            opacity: dimmed ? 0.22 : 1,
            transition: 'opacity 400ms ease',
            maxWidth: '96vw'
          }}
        >
          {current !== null
            ? (
              <div
                key={current.seq}
                data-testid='camctl-current'
                data-seq={current.seq}
                style={{
                  fontSize: commandFontSize(current.command),
                  fontWeight: 700,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  animation: 'spCamPulse 700ms ease-out',
                  textShadow: '0 0 40px rgba(103,232,249,0.35)'
                }}
              >
                {current.command}
              </div>
              )
            : (
              <div
                data-testid='camctl-waiting'
                style={{ fontSize: 'min(6vh, 4vw)', color: 'rgba(255,255,255,0.45)' }}
              >
                {status === null ? 'Connecting…' : 'Waiting for chat…'}
              </div>
              )}
          {current !== null && current.streak > 1 && (
            <div
              data-testid='camctl-streak'
              style={{
                fontSize: 'min(5vh, 3.5vw)',
                fontWeight: 600,
                color: '#67e8f9',
                border: '2px solid rgba(103,232,249,0.5)',
                borderRadius: 9999,
                padding: '0.2em 0.9em'
              }}
            >
              x{current.streak}
            </div>
          )}
        </div>

        {upcoming !== null && (
          <div
            data-testid='camctl-next'
            style={{
              position: 'absolute',
              bottom: '5vh',
              fontSize: 'min(4vh, 2.6vw)',
              color: 'rgba(255,255,255,0.5)'
            }}
          >
            next: {upcoming.command} in {(Math.max(0, upcoming.showAt - now) / 1000).toFixed(1)}s
          </div>
        )}
      </div>
    </OverlayShell>
  )
}
